import React from 'react';
import {
  ApplicationIcon,
  Button,
  Icon,
  ModalIsland,
} from '@saviynt/design-system';

import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
} from '../../../../constants/obNextGenConstants';
import { useLocalization } from '../../../../hooks';

import './ApplicationOnboardingModal.css';

interface ApplicationOnboardingModalProps {
  isOpen: boolean;
  applicationName: string;
  applicationDescription: string;
  applicationIcon: string;
  handleClose: () => void;
  handleSelectDifferentApplication: () => void;
  handleConfirmApplicationOnboarding: () => void;
}

/**
 * Application Onboarding Modal
 */
function ApplicationOnboardingModal({
  isOpen,
  handleClose,
  applicationName,
  applicationDescription,
  applicationIcon,
  handleSelectDifferentApplication,
  handleConfirmApplicationOnboarding,
}: ApplicationOnboardingModalProps) {
  const formatMessage = useLocalization();

  const baseUrl = getBaseUrlObNextGen();
  const { GET_APPLICATION_ICONS } = getObNextGenApiUrlLists();

  return (
    <ModalIsland
      kind='default'
      isOpen={isOpen}
      onClose={handleClose}
      title={formatMessage(
        'INTEGRATIONS.DASHBOARD.APPLICATION_ONBOARDING.TITLE',
        'Application Onboarding Confirmation'
      )}
      headerIcon={<Icon kind='ManageOverview' size='large' />}
      className='ApplicationOnboardingModal'
      contentBody={
        <div className='ApplicationOnboardingModal-content'>
          <p className='ApplicationOnboardingModal-desc'>
            {formatMessage(
              'INTEGRATIONS.DASHBOARD.APPLICATION_ONBOARDING.DESCRIPTION',
              'We’ve identified the application type. Please confirm if this is the right application type you’re onboarding.'
            )}
          </p>
          <div className='ApplicationOnboardingModal-card'>
            <div className='ApplicationOnboardingModal-card-header'>
              <div className='ApplicationOnboardingModal-card-icon'>
                {applicationIcon ? (
                  <img
                    src={`${baseUrl}${GET_APPLICATION_ICONS}/${applicationIcon}`}
                    alt='Application icon'
                    className='ApplicationOnboardingModal-Image'
                  />
                ) : (
                  <ApplicationIcon size='large' logo={applicationIcon} />
                )}
              </div>
              <span className='ApplicationOnboardingModal-card-title'>
                {applicationName}
              </span>
            </div>
            <p className='ApplicationOnboardingModal-card-desc'>
              {applicationDescription
                ? formatMessage(
                    applicationDescription,
                    `The application type you're onboarding is ${applicationName}`
                  )
                : `The application type you're onboarding is ${applicationName}`}
            </p>
          </div>
        </div>
      }
      FooterActionBarComp={
        <div className='ApplicationOnboardingModal-footer'>
          <Button
            onClick={handleSelectDifferentApplication}
            type='button'
            kind='outlined'
            size='medium'
            dataTestId='integrations-onboarding-application-list'>
            {formatMessage(
              'INTEGRATIONS.DASHBOARD.APPLICATION_ONBOARDING.SELECT_DIFFERENT',
              'No, select from an application list'
            )}
          </Button>
          <Button
            type='button'
            kind='filled'
            size='medium'
            onClick={handleConfirmApplicationOnboarding}
            dataTestId={`integrations-onboarding-${applicationName}`}>
            {`${formatMessage(
              'INTEGRATIONS.DASHBOARD.APPLICATION_ONBOARDING.CONFIRM',
              "Yes, I'm onboarding"
            )} ${applicationName}`}
          </Button>
        </div>
      }
    />
  );
}

export default ApplicationOnboardingModal;
